import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function formatMx(m) {
  if (m === 1) return "x";
  if (m === -1) return "-x";
  return `${m}x`;
}

function toLatexPolynomial({ a, b, c }) {
  const parts = [];

  const pushTerm = (coef, base) => {
    if (coef === 0) return;
    const abs = Math.abs(coef);
    const sign = coef < 0 ? "-" : "+";
    const coefStr = abs === 1 && base ? "" : `${abs}`;
    const body = base ? `${coefStr}${base}` : `${abs}`;
    parts.push({ sign, body });
  };

  pushTerm(a, "x^2");
  pushTerm(b, "x");
  pushTerm(c, "");

  if (!parts.length) return "0";
  const [first, ...rest] = parts;
  const firstText = `${first.sign === "-" ? "-" : ""}${first.body}`;
  const restText = rest.map((t) => ` ${t.sign} ${t.body}`).join("");
  return `${firstText}${restText}`;
}

function parsePolynomial(input) {
  const raw = String(input ?? "")
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .replace(/−/g, "-")
    .toLowerCase();

  if (!raw) return null;

  const normalized = raw[0] === "-" ? raw : `+${raw}`;
  const terms = normalized.match(/[+-][^+-]+/g);
  if (!terms) return null;

  let a = 0;
  let b = 0;
  let c = 0;

  const parseCoeff = (txt) => {
    if (txt === "" || txt === "+") return 1;
    if (txt === "-") return -1;
    const n = Number(txt);
    return Number.isFinite(n) ? n : null;
  };

  for (const term of terms) {
    if (term.includes("x^2")) {
      const coeffTxt = term.replace("x^2", "");
      const coeff = parseCoeff(coeffTxt);
      if (coeff === null) return null;
      a += coeff;
      continue;
    }
    if (term.includes("x")) {
      const coeffTxt = term.replace("x", "");
      const coeff = parseCoeff(coeffTxt);
      if (coeff === null) return null;
      b += coeff;
      continue;
    }
    const n = Number(term);
    if (!Number.isFinite(n)) return null;
    c += n;
  }

  return { a, b, c };
}

function makeComponent(kind) {
  const chosenKind = kind ?? pick(["sumSquare", "diffSquare", "diffSquares"]);
  const m = randInt(1, 4);
  const n = randInt(1, 9);
  const mx = formatMx(m);

  if (chosenKind === "sumSquare") {
    return {
      kind: chosenKind,
      expression: `(${mx} + ${n})^2`,
      expanded: { a: m * m, b: 2 * m * n, c: n * n },
      formula: "(a+b)^2=a^2+2ab+b^2",
    };
  }

  if (chosenKind === "diffSquare") {
    return {
      kind: chosenKind,
      expression: `(${mx} - ${n})^2`,
      expanded: { a: m * m, b: -2 * m * n, c: n * n },
      formula: "(a-b)^2=a^2-2ab+b^2",
    };
  }

  return {
    kind: chosenKind,
    expression: `(${mx} - ${n})(${mx} + ${n})`,
    expanded: { a: m * m, b: 0, c: -(n * n) },
    formula: "(a-b)(a+b)=a^2-b^2",
  };
}

export function generateTask() {
  const kinds = ["sumSquare", "diffSquare", "diffSquares"];

  for (let i = 0; i < 30; i += 1) {
    const leftKind = pick(kinds);
    const rightKind = pick(kinds.filter((k) => k !== leftKind)); // zawsze inny typ wzoru

    const left = makeComponent(leftKind);
    const right = makeComponent(rightKind);
    const op = Math.random() < 0.5 ? "+" : "-";
    const sign = op === "+" ? 1 : -1;

    const result = {
      a: left.expanded.a + sign * right.expanded.a,
      b: left.expanded.b + sign * right.expanded.b,
      c: left.expanded.c + sign * right.expanded.c,
    };

    // Dodatkowy bezpiecznik: odrzucamy przypadek identycznego zapisu i wynik zerowy.
    if (
      left.expression === right.expression ||
      (result.a === 0 && result.b === 0 && result.c === 0)
    ) {
      continue;
    }

    return {
      type: "MixedSpecialProductsTask",
      data: {
        left,
        right,
        op,
        expectedCoeffs: result,
        expected: toLatexPolynomial(result),
        expression: `${left.expression} ${op} ${right.expression}`,
      },
    };
  }

  // Awaryjny fallback (też na dwóch różnych typach), gdyby pętla nie zwróciła wyniku.
  const left = makeComponent("sumSquare");
  const right = makeComponent("diffSquare");
  const op = "+";
  const result = {
    a: left.expanded.a + right.expanded.a,
    b: left.expanded.b + right.expanded.b,
    c: left.expanded.c + right.expanded.c,
  };
  return {
    type: "MixedSpecialProductsTask",
    data: {
      left,
      right,
      op,
      expectedCoeffs: result,
      expected: toLatexPolynomial(result),
      expression: `${left.expression} ${op} ${right.expression}`,
    },
  };
}

export function renderTask({ data }) {
  return (
    <p>
      Uprość wyrażenie:{" "}
      <InlineMath math={data.expression} />
    </p>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string") {
    return { isCorrect: false, expected: data.expected };
  }

  const parsed = parsePolynomial(userAnswer);
  if (!parsed) return { isCorrect: false, expected: data.expected };

  const ok =
    parsed.a === data.expectedCoeffs.a &&
    parsed.b === data.expectedCoeffs.b &&
    parsed.c === data.expectedCoeffs.c;

  return { isCorrect: ok, expected: data.expected };
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.expected} />;
}

export function renderExplanation({ data }) {
  const leftExpanded = toLatexPolynomial(data.left.expanded);
  const rightExpanded = toLatexPolynomial(data.right.expanded);
  const afterSubstitution = `${leftExpanded} ${data.op} (${rightExpanded})`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Rozpoznajemy wzory:
      </p>
      <BlockMath math={`${data.left.formula}\\qquad ${data.right.formula}`} />

      <p>
        <strong>Krok 2:</strong> Rozwijamy każdy składnik:
      </p>
      <BlockMath
        math={`${data.left.expression}=${leftExpanded}\\qquad ${data.right.expression}=${rightExpanded}`}
      />

      <p>
        <strong>Krok 3:</strong> Podstawiamy i redukujemy wyrazy podobne:
      </p>
      <BlockMath math={`${data.expression}=${afterSubstitution}=${data.expected}`} />
    </div>
  );
}

export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}
